/**
 * Created by neo on 10.01.2026
 * Challenge Leaderboard Component - Shows ranking of teams or participants
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { RouteChallengeTeam } from '../../../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { IndividualChallengeParticipant } from '../../../Model/Engagement/IndividualChallenge/IndividualChallengeParticipant';
import { Theme } from '../../../Theme/Theme';
import type { ChallengeType } from '../ChallengeDetailScreen';
import { getMediaSmallest } from '../../../Model/Media/Media';
import type { RouteChallenge } from '../../../Model/Engagement/RouteChallenge/RouteChallenge';
import type { IndividualChallenge } from '../../../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { ChallengeExtraMile } from './ChallengeExtraMile';
import { ChallengeTeamMembers } from './ChallengeTeamMembers';

const LeaderboardContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LeaderboardItem = styled.div<{ isCurrentUser: boolean; rank: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: ${(props) => {
    if (props.isCurrentUser) return Theme.Colors.lightestPrimary;
    if (props.rank === 1) return 'linear-gradient(135deg, #FFF9E6 0%, #FFF3CC 100%)';
    if (props.rank === 2) return 'linear-gradient(135deg, #F5F5F5 0%, #E8E8E8 100%)';
    if (props.rank === 3) return 'linear-gradient(135deg, #FDF4EB 0%, #FAEADB 100%)';
    return Theme.Colors.lightestGrey;
  }};
  border: ${(props) => (props.isCurrentUser ? `2px solid ${Theme.Colors.primary}` : 'none')};
`;

const RankBadge = styled.div<{ rank: number }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: ${(props) => (props.rank <= 3 ? '16px' : '13px')};
  background: ${(props) => {
    if (props.rank === 1) return '#FFD700';
    if (props.rank === 2) return '#C0C0C0';
    if (props.rank === 3) return '#CD7F32';
    return Theme.Colors.lighterGrey;
  }};
  color: ${(props) => (props.rank <= 3 ? 'white' : Theme.Colors.charcoal)};
  flex-shrink: 0;
`;

const Avatar = styled.div<{ imageUrl?: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(props) => (props.imageUrl ? `url(${props.imageUrl})` : Theme.Colors.primary)};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 16px;
  flex-shrink: 0;
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemSubtext = styled.div`
  font-size: 12px;
  color: ${Theme.Colors.warmGrey};
`;

const PointsValue = styled.div`
  text-align: right;
  flex-shrink: 0;
`;

const PointsNumber = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
`;

const PointsLabel = styled.div`
  font-size: 10px;
  color: ${Theme.Colors.warmGrey};
  text-transform: uppercase;
`;

const FinishedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: linear-gradient(135deg, #10B981 0%, #059669 100%);
  color: white;
  margin-left: 8px;
  white-space: nowrap;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
`;

const ItemNameRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${Theme.Colors.charcoal};
`;

const EmptyEmoji = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

export type ChallengeLeaderboardProps = {
  challenge: RouteChallenge | IndividualChallenge;
  challengeType: ChallengeType;
  teams: RouteChallengeTeam[];
  participants: IndividualChallengeParticipant[];
  myTeam: RouteChallengeTeam | null;
  myParticipation: IndividualChallengeParticipant | null;
};

export const ChallengeLeaderboard: React.FC<ChallengeLeaderboardProps> = observer(
  ({ challenge, challengeType, teams, participants, myTeam, myParticipation }) => {
    const { t } = useTrans();

    const isIndividual = challengeType === 'individual';
    const items = isIndividual ? participants : teams;

    if (items.length === 0) {
      return (
        <LeaderboardContainer>
          <SectionTitle>
            <span>🏆</span>
            {t('challenge.leaderboard.title', { defaultValue: 'Leaderboard' })}
          </SectionTitle>
          <EmptyState>
            <EmptyEmoji>🏅</EmptyEmoji>
            <div>
              {isIndividual
                ? t('challenge.leaderboard.noParticipants', { defaultValue: 'No participants yet' })
                : t('challenge.leaderboard.noTeams', { defaultValue: 'No teams yet' })}
            </div>
          </EmptyState>
        </LeaderboardContainer>
      );
    }

    return (
      <LeaderboardContainer>
        <SectionTitle>
          <span>🏆</span>
          {t('challenge.leaderboard.title', { defaultValue: 'Leaderboard' })}
        </SectionTitle>

        <LeaderboardList>
          {isIndividual
            ? participants.slice(0, 10).map((participant, index) => {
                const rank = participant.rank || index + 1;
                const isCurrentUser = myParticipation?.id === participant.id;
                const displayName = participant.nickname || t('challenge.anonymous', { defaultValue: 'Participant' });

                const hasFinished = participant.hasCompletedChallenge;

                return (
                  <LeaderboardItem key={participant.id} isCurrentUser={isCurrentUser} rank={rank}>
                    <RankBadge rank={rank}>
                      {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                    </RankBadge>
                    <Avatar imageUrl={participant.profilePicture ? getMediaSmallest(participant.profilePicture) : undefined}>
                      {!participant.profilePicture && displayName.charAt(0).toUpperCase()}
                    </Avatar>
                    <ItemContent>
                      <ItemNameRow>
                        <ItemName>{displayName}</ItemName>
                        {hasFinished && (
                          <FinishedBadge>
                            🏁 {t('challenge.leaderboard.finished', { defaultValue: 'Finished' })}
                          </FinishedBadge>
                        )}
                      </ItemNameRow>
                      <ItemSubtext>
                        {participant.reachedLocationsCount}{' '}
                        {t('challenge.leaderboard.locationsReached', { defaultValue: 'locations' })}
                      </ItemSubtext>
                      <ChallengeExtraMile challenge={challenge} totalPoints={participant.totalCollectedPoints} goalPoints={challenge.totalPossiblePoints} variant="ranking" />
                    </ItemContent>
                    <PointsValue>
                      <PointsNumber>{participant.totalCollectedPoints.toLocaleString()}</PointsNumber>
                      <PointsLabel>{t('challenge.leaderboard.pts', { defaultValue: 'pts' })}</PointsLabel>
                    </PointsValue>
                  </LeaderboardItem>
                );
              })
            : teams.slice(0, 10).map((team, index) => {
                const rank = team.globalRank || index + 1;
                const isCurrentUser = myTeam?.id === team.id;

                const hasFinished = !!team.completedDateTime;

                return (
                  <LeaderboardItem key={team.id} isCurrentUser={isCurrentUser} rank={rank}>
                    <RankBadge rank={rank}>
                      {rank <= 3 ? ['🥇', '🥈', '🥉'][rank - 1] : rank}
                    </RankBadge>
                    <Avatar imageUrl={team.image ? getMediaSmallest(team.image) : undefined}>
                      {!team.image && team.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <ItemContent>
                      <ItemNameRow>
                        <ItemName><ChallengeTeamMembers team={team} challenge={challenge} /></ItemName>
                        {hasFinished && (
                          <FinishedBadge>
                            🏁 {t('challenge.leaderboard.finished', { defaultValue: 'Finished' })}
                          </FinishedBadge>
                        )}
                      </ItemNameRow>
                      <ItemSubtext>
                        {team.memberCount} {t('challenge.leaderboard.members', { defaultValue: 'members' })}
                      </ItemSubtext>
                      <ChallengeExtraMile challenge={challenge} totalPoints={team.totalCollectedPoints} goalPoints={challenge.totalPossiblePoints} variant="ranking" />
                    </ItemContent>
                    <PointsValue>
                      <PointsNumber>{team.totalCollectedPoints.toLocaleString()}</PointsNumber>
                      <PointsLabel>{t('challenge.leaderboard.pts', { defaultValue: 'pts' })}</PointsLabel>
                    </PointsValue>
                  </LeaderboardItem>
                );
              })}
        </LeaderboardList>
      </LeaderboardContainer>
    );
  },
);
